import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import { useTranslation } from 'react-i18next';

import { cn } from '@/lib/utils';
import { getDateLocale } from '@/utils/date';

/*
 * Calendar (date picker primitive) — wraps react-day-picker with design-system
 * styling. This is the *date-picker* surface, NOT the schedule view. For the
 * unified schedule Calendar see @/components/calendar/Calendar.
 */

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  const { i18n } = useTranslation();
  return (
    <DayPicker
      locale={getDateLocale(i18n.language)}
      weekStartsOn={1}
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-ink',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          'inline-flex items-center justify-center rounded-md text-sm font-medium',
          'border border-line bg-surface text-soft hover:border-brand-300 hover:text-brand-500',
          'h-7 w-7 p-0 transition-colors',
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse space-y-1',
        head_row: 'flex',
        head_cell: 'text-soft rounded-md w-9 font-normal text-[0.8rem]',
        row: 'flex w-full mt-2',
        cell: cn(
          'h-9 w-9 text-center text-sm p-0 relative',
          '[&:has([aria-selected])]:bg-brand-50',
          'first:[&:has([aria-selected])]:rounded-l-md',
          'last:[&:has([aria-selected])]:rounded-r-md',
          'focus-within:relative focus-within:z-20',
        ),
        day: cn(
          'inline-flex items-center justify-center rounded-md text-sm',
          'text-ink hover:bg-surface-2 transition-colors',
          'h-9 w-9 p-0 font-normal aria-selected:opacity-100',
        ),
        day_selected:
          'bg-brand-500 text-white hover:bg-brand-700 focus:bg-brand-500 focus:text-white',
        day_today: 'bg-brand-100 text-brand-700 font-semibold',
        day_outside: 'text-text-muted opacity-50',
        day_disabled: 'text-text-muted opacity-50',
        day_range_middle: 'aria-selected:bg-brand-50 aria-selected:text-ink',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
         
        IconLeft: (_props) => <ChevronLeft className="h-4 w-4" />,
         
        IconRight: (_props) => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
